/** Stream tags for creator payout ledger rows (D-P-009, D-P-019). */
export const COMMERCE_LEDGER_STREAMS = [
  'circle_membership',
  'course_purchase',
  'creator_membership',
  'service_digital',
  'service_booking',
  'service_priority_dm',
  'service_webinar',
  'service_cohort',
  'service_package',
  'system',
] as const;

export type CommerceLedgerStream = (typeof COMMERCE_LEDGER_STREAMS)[number];

export const COMMERCE_LEDGER_EVENT_TYPES = [
  'sale_credit',
  'refund_debit',
  'dispute_debit',
  'fee_debit',
  'hold',
  'hold_release',
  'payout_debit',
] as const;

export type CommerceLedgerEventType = (typeof COMMERCE_LEDGER_EVENT_TYPES)[number];

/** Checkout metadata `flow` → ledger stream. */
export function checkoutFlowToLedgerStream(flow: string | null | undefined): CommerceLedgerStream | null {
  switch (flow) {
    case 'circle_membership':
      return 'circle_membership';
    case 'creator_membership':
      return 'creator_membership';
    case 'course_purchase':
      return 'course_purchase';
    case 'service_digital':
      return 'service_digital';
    case 'service_one_to_one':
      return 'service_booking';
    case 'service_priority_dm':
      return 'service_priority_dm';
    case 'service_webinar':
      return 'service_webinar';
    case 'service_cohort':
      return 'service_cohort';
    case 'service_package':
      return 'service_package';
    default:
      return null;
  }
}

export function serviceOfferTypeToLedgerStream(type: string): CommerceLedgerStream | null {
  switch (type) {
    case 'digital':
      return 'service_digital';
    case 'one_to_one':
      return 'service_booking';
    case 'priority_dm':
      return 'service_priority_dm';
    case 'webinar':
      return 'service_webinar';
    case 'cohort':
      return 'service_cohort';
    case 'package':
      return 'service_package';
    default:
      return null;
  }
}

/** Creator net credited on Connect before payouts (D-P-010). */
export function computeCreatorNetCents(grossCents: number, platformFeeCents: number): number {
  return Math.max(0, grossCents - platformFeeCents);
}

export function isLifetimeEarningsEvent(eventType: CommerceLedgerEventType): boolean {
  return eventType === 'sale_credit';
}

export function commerceLedgerStreamLabel(stream: CommerceLedgerStream): string {
  switch (stream) {
    case 'circle_membership':
      return 'Circle';
    case 'course_purchase':
      return 'Course';
    case 'creator_membership':
      return 'Membership';
    case 'service_digital':
      return 'Digital service';
    case 'service_booking':
      return '1:1 booking';
    case 'service_priority_dm':
      return 'Priority DM';
    case 'service_webinar':
      return 'Webinar';
    case 'service_cohort':
      return 'Cohort';
    case 'service_package':
      return 'Package';
    case 'system':
      return 'System';
    default:
      return stream;
  }
}

export function commerceLedgerEventTypeLabel(eventType: CommerceLedgerEventType): string {
  switch (eventType) {
    case 'sale_credit':
      return 'Sale';
    case 'refund_debit':
      return 'Refund';
    case 'dispute_debit':
      return 'Dispute';
    case 'fee_debit':
      return 'Fee';
    case 'hold':
      return 'Hold';
    case 'hold_release':
      return 'Hold released';
    case 'payout_debit':
      return 'Payout';
    default:
      return eventType;
  }
}

/** Studio stream filter options (excludes system rows). */
export const COMMERCE_LEDGER_STUDIO_STREAMS = COMMERCE_LEDGER_STREAMS.filter(
  (stream) => stream !== 'system',
);

/** Studio type filter options. */
export const COMMERCE_LEDGER_STUDIO_EVENT_TYPES = COMMERCE_LEDGER_EVENT_TYPES.filter(
  (eventType) => eventType !== 'fee_debit',
);

/** Default online ledger / CSV window (D-P-OPEN-02). */
export const PAYOUT_LEDGER_EXPORT_MONTHS = 24;

export const PAYOUT_LEDGER_EXPORT_MAX_ROWS = 10_000;

/** LOAMIFY withdrawable = Stripe available minus open holds (P-031). */
export function computeWithdrawableCents(
  stripeAvailableCents: number,
  activeHoldsCents: number,
): number {
  return Math.max(0, stripeAvailableCents - activeHoldsCents);
}

export function validatePayoutWithdrawAmountCents(
  amountCents: number | undefined,
  withdrawableCents: number,
): number {
  const payoutAmountCents = amountCents ?? withdrawableCents;
  if (payoutAmountCents <= 0) {
    throw new Error('Nothing available to withdraw');
  }
  if (payoutAmountCents > withdrawableCents) {
    throw new Error('Withdrawal amount exceeds withdrawable balance after open holds');
  }
  return payoutAmountCents;
}

export function shouldWarnHoldsWithAutomaticPayouts(
  activeHoldsCents: number,
  manualPayoutsEnabled: boolean,
): boolean {
  return activeHoldsCents > 0 && !manualPayoutsEnabled;
}

export type ConnectPayoutScheduleInterval = 'daily' | 'weekly' | 'monthly' | 'manual';

export function formatConnectPayoutScheduleLabel(schedule: {
  interval: ConnectPayoutScheduleInterval | string;
  weeklyPayoutDays?: string[] | null;
  monthlyPayoutDays?: number[] | null;
}): string {
  switch (schedule.interval) {
    case 'manual':
      return 'Manual — withdraw when you\'re ready';
    case 'daily':
      return 'Automatic daily payouts';
    case 'weekly':
      if (schedule.weeklyPayoutDays?.length) {
        const days = schedule.weeklyPayoutDays.map((day) => day.charAt(0).toUpperCase() + day.slice(1));
        return `Automatic weekly payouts (${days.join(', ')})`;
      }
      return 'Automatic weekly payouts';
    case 'monthly':
      if (schedule.monthlyPayoutDays?.length) {
        return `Automatic monthly payouts (days ${schedule.monthlyPayoutDays.join(', ')})`;
      }
      return 'Automatic monthly payouts';
    default:
      return 'Automatic payouts';
  }
}
